/**
 * Passport graphics (DECISIONS #101): a waving flag, the passport cover, a
 * passport page and the rubber stamp. Pure presentation — data comes from the
 * caller. Styles: `passport.css`.
 */
import { useEffect, useRef, type CSSProperties, type ReactNode } from 'react';
import { loadFlagUrl } from '@/lib/country';
import { useT } from '@/i18n/react';
import './passport.css';

/** Stable small number from a country code — ink colour, tilt, wave phase. */
function seedOf(iso: string): number {
  let hash = 0;
  for (const char of iso) hash = (hash * 31 + char.charCodeAt(0)) >>> 0;
  return hash;
}

export function stampLook(iso: string): { ink: 1 | 2 | 3 | 4; rotateDeg: number } {
  const seed = seedOf(iso);
  return { ink: ((seed % 4) + 1) as 1 | 2 | 3 | 4, rotateDeg: (seed % 19) - 11 };
}

/* ── Waving flag: drawn on a canvas, one device-pixel column at a time ── */

const FLAG_WIDTH = { sm: 44, md: 72, lg: 128 } as const;
/** Wave height as a share of the flag height (at the free end). */
const WAVE_AMPLITUDE = 0.065;
/** One wave across this share of the flag width. */
const WAVE_LENGTH = 0.85;
const WAVE_PERIOD_MS = 2600;

type Painter = (now: number) => void;
const painters = new Set<Painter>();
let frame = 0;

/** One animation loop for every flag on screen; it stops when none is left. */
function tick(now: number) {
  for (const paint of painters) paint(now);
  frame = painters.size > 0 ? requestAnimationFrame(tick) : 0;
}

function animate(paint: Painter): () => void {
  painters.add(paint);
  if (!frame) frame = requestAnimationFrame(tick);
  return () => {
    painters.delete(paint);
  };
}

function prefersReducedMotion(): boolean {
  return typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/**
 * A flag whose cloth really waves: every column of pixels is shifted along a
 * sine wave (more at the free end than at the fixed side) and lit by the slope
 * of the fold. The flag image is rasterised once at the device resolution;
 * each frame only copies 1-pixel slices of it, so the edges stay smooth. Flags
 * off screen pause; with reduced motion the flag is drawn once, still.
 */
export function WavingFlag({ iso, size = 'md', still = false }: { iso: string; size?: 'sm' | 'md' | 'lg'; still?: boolean }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const width = FLAG_WIDTH[size];
  const height = Math.round(width * 0.75);
  const pad = Math.ceil(height * WAVE_AMPLITUDE) + 2;

  useEffect(() => {
    const canvas = canvasRef.current;
    const load = loadFlagUrl(iso);
    if (!canvas || !load) return;
    let alive = true;
    let stop: (() => void) | null = null;
    let observer: IntersectionObserver | null = null;

    void load.then((url) => {
      const image = new Image();
      image.decoding = 'async';
      image.onload = () => {
        if (!alive) return;
        const ratio = Math.min(3, window.devicePixelRatio || 1);
        const cols = Math.round(width * ratio);
        const rows = Math.round(height * ratio);
        const padPx = Math.round(pad * ratio);
        canvas.width = cols;
        canvas.height = rows + padPx * 2;

        // Rasterise the (SVG) flag once at the final resolution.
        const source = document.createElement('canvas');
        source.width = cols;
        source.height = rows;
        const sourceContext = source.getContext('2d');
        const context = canvas.getContext('2d');
        if (!sourceContext || !context) return;
        sourceContext.drawImage(image, 0, 0, cols, rows);

        const phase = (seedOf(iso) % 360) * (Math.PI / 180);
        const paint = (now: number) => {
          const t = (now / WAVE_PERIOD_MS) * Math.PI * 2 + phase;
          context.clearRect(0, 0, canvas.width, canvas.height);
          for (let x = 0; x < cols; x++) {
            const along = x / cols;
            const angle = t - (along * Math.PI * 2) / WAVE_LENGTH;
            // The fixed side barely moves; the free end swings fully.
            const reach = 0.12 + 0.88 * along;
            const offset = Math.sin(angle) * rows * WAVE_AMPLITUDE * reach;
            // Slightly wider than one column, so no hairline gap shows.
            context.drawImage(source, x, 0, 1, rows, x, padPx + offset, 1.6, rows);
            const light = Math.cos(angle) * reach * 0.22;
            if (light > 0.01) context.fillStyle = `rgba(255, 255, 255, ${light.toFixed(3)})`;
            else if (light < -0.01) context.fillStyle = `rgba(0, 0, 0, ${(-light).toFixed(3)})`;
            else continue;
            context.fillRect(x, padPx + offset, 1, rows);
          }
        };

        paint(0);
        if (still || prefersReducedMotion()) return;
        observer = new IntersectionObserver(([entry]) => {
          if (entry?.isIntersecting) stop ??= animate(paint);
          else {
            stop?.();
            stop = null;
          }
        });
        observer.observe(canvas);
      };
      image.src = url;
    });

    return () => {
      alive = false;
      stop?.();
      observer?.disconnect();
    };
  }, [iso, width, height, pad, still]);

  return (
    <span className={`wflag wflag--${size}`} aria-hidden="true">
      <canvas
        ref={canvasRef}
        className="wflag__canvas"
        style={{ width, height: height + pad * 2 } as CSSProperties}
      />
    </span>
  );
}

/**
 * A not-yet-unlocked country: a still, greyed flag. It loads its image only
 * when it scrolls into view — the grid holds every country.
 */
export function LockedFlag({ iso }: { iso: string }) {
  const imageRef = useRef<HTMLImageElement>(null);

  useEffect(() => {
    const image = imageRef.current;
    if (!image) return;
    let alive = true;
    const observer = new IntersectionObserver(([entry]) => {
      if (!entry?.isIntersecting) return;
      observer.disconnect();
      void loadFlagUrl(iso)?.then((url) => {
        if (alive) image.src = url;
      });
    }, { rootMargin: '200px' });
    observer.observe(image);
    return () => {
      alive = false;
      observer.disconnect();
    };
  }, [iso]);

  return <img ref={imageRef} className="wflag__locked" alt="" aria-hidden="true" />;
}

/**
 * The GRUNDO mark — the real logo (outer hexagon + the open hexagonal G),
 * traced from the brand PNG onto a 1024 grid, in foil (`currentColor`).
 */
function Crest() {
  return (
    <svg className="pcover__crest" viewBox="0 0 1024 1024" aria-hidden="true">
      <g fill="none" stroke="currentColor" strokeWidth="100" strokeLinecap="round" strokeLinejoin="round">
        <polygon points="104,512 318,155 706,155 920,512 706,869 318,869" />
        <polyline points="612,337 410,337 312,512 410,687 612,687 712,512" />
      </g>
    </svg>
  );
}

export function PassportCover({ size = 'md' }: { size?: 'sm' | 'md' | 'lg' }) {
  const t = useT().passport;
  return (
    <div className={`pcover${size === 'md' ? '' : ` pcover--${size}`}`} aria-hidden="true">
      <span className="pcover__title">{t.coverTitle}</span>
      <Crest />
      <span className="pcover__subtitle">{t.coverSubtitle}</span>
      <span className="pcover__brand">GRUNDO</span>
    </div>
  );
}

export function PassportPage({ children, pageNumber }: { children: ReactNode; pageNumber?: number }) {
  const t = useT().passport;
  return (
    <div className="ppage">
      <div className="ppage__head" aria-hidden="true">
        <span>{t.pageTitle}</span>
        <span>GRUNDO</span>
      </div>
      <div className="ppage__body">{children}</div>
      <div className="ppage__foot" aria-hidden="true">{pageNumber ?? ''}</div>
    </div>
  );
}

export function PassportStamp({
  iso,
  name,
  date,
  slam = false,
  slamDelayMs = 0,
}: {
  iso: string;
  name: string;
  date: string;
  slam?: boolean;
  slamDelayMs?: number;
}) {
  const t = useT().passport;
  const look = stampLook(iso);
  return (
    <div
      className={`pstamp${look.ink === 1 ? '' : ` pstamp--ink${look.ink}`}${slam ? ' pstamp--slam' : ''}`}
      style={{ '--rot': `${look.rotateDeg}deg`, '--slam-delay': `${slamDelayMs}ms` } as CSSProperties}
      role="img"
      aria-label={t.stampLabel(name)}
    >
      <span className="pstamp__top">GRUNDO · {iso}</span>
      <span className="pstamp__name">{name}</span>
      <span className="pstamp__approved">{t.approved}</span>
      <span className="pstamp__date">{date}</span>
    </div>
  );
}
